import { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Alert,
  Box, TextField, IconButton, Tooltip, Typography,
} from '@mui/material';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';

type Props = {
  result: { client_id: string; client_secret: string; name: string } | null;
  onClose: () => void;
};

export function NewClientDialog({ result, onClose }: Props) {
  const [copiedId, setCopiedId] = useState(false);
  const [copiedSecret, setCopiedSecret] = useState(false);

  useEffect(() => {
    setCopiedId(false);
    setCopiedSecret(false);
  }, [result]);

  const copy = async (text: string, which: 'id' | 'secret') => {
    try {
      await navigator.clipboard.writeText(text);
      if (which === 'id') setCopiedId(true);
      else setCopiedSecret(true);
      setTimeout(() => {
        if (which === 'id') setCopiedId(false);
        else setCopiedSecret(false);
      }, 1500);
    } catch { /* ignore */ }
  };

  return (
    <Dialog
      open={result !== null}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
    >
      <DialogTitle sx={{ fontFamily: 'Fraunces Variable', fontSize: '1.5rem' }}>
        Your new OAuth client
      </DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 3, borderRadius: 0 }}>
          This is the only time you'll see the client secret. Copy it now and store
          it somewhere secure — it cannot be retrieved later (only rotated).
        </Alert>
        {result && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              <Typography variant="caption" sx={{ color: 'var(--ink-muted)', display: 'block', mb: 0.5 }}>
                Name
              </Typography>
              <Typography sx={{ fontFamily: 'IBM Plex Mono', fontSize: '0.875rem' }}>
                {result.name}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: 'var(--ink-muted)', display: 'block', mb: 0.5 }}>
                Client ID
              </Typography>
              <CopyField value={result.client_id} copied={copiedId} onCopy={() => copy(result.client_id, 'id')} label="client-id" />
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: 'var(--ink-muted)', display: 'block', mb: 0.5 }}>
                Client Secret
              </Typography>
              <CopyField value={result.client_secret} copied={copiedSecret} onCopy={() => copy(result.client_secret, 'secret')} label="client-secret" />
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">I've copied it — close</Button>
      </DialogActions>
    </Dialog>
  );
}

function CopyField({ value, copied, onCopy, label }: { value: string; copied: boolean; onCopy: () => void; label: string }) {
  return (
    <Box sx={{ position: 'relative' }}>
      <TextField
        value={value}
        fullWidth
        multiline
        minRows={1}
        inputProps={{ id: `${label}-field` }}
        InputProps={{
          readOnly: true,
          sx: { fontFamily: 'IBM Plex Mono', fontSize: '0.8125rem', pr: 5 },
        }}
        onFocus={(e) => e.target.select()}
      />
      <Tooltip title={copied ? 'Copied' : 'Copy'}>
        <IconButton
          onClick={onCopy}
          size="small"
          sx={{
            position: 'absolute',
            top: 6,
            right: 6,
            borderRadius: 0,
            border: '1px solid var(--rule)',
            backgroundColor: 'var(--bg)',
          }}
          aria-label={copied ? `${label} copied` : `Copy ${label}`}
        >
          {copied ? <CheckOutlinedIcon fontSize="small" /> : <ContentCopyOutlinedIcon fontSize="small" />}
        </IconButton>
      </Tooltip>
    </Box>
  );
}